import { Box, Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RecipientPicker } from '@/components/recipient-picker';
import { useInvitationActions, useInvitationStoreSelector } from '@/contexts/invitation-context';
import { useAffiliateRecipientSearch } from '@/hooks/queries/use-affiliate-recipient-search';

/**
 * First step of the `smart-input` invitation wizard: pick an existing affiliate
 * or type a brand-new email. Both routes call `setTarget`, which advances the
 * store to `programSelection` — same downstream as before. The shared
 * RecipientPicker replaces the old smart-input + affiliate-results + email-form
 * trio; the affiliate search (via `useAffiliateRecipientSearch`) stays the source
 * (strategy B), the raw-email row replaces the standalone email form.
 */
export const InputSelectionView = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { setTarget, setAffiliateAddDraft } = useInvitationActions();
  const entryIntent = useInvitationStoreSelector((state) => state.entryIntent);

  const { affiliates, options, emptyHint, isLoading, isFetching } = useAffiliateRecipientSearch(
    search,
    { i18nPrefix: 'invitations.modal.inputSelection' }
  );

  const handleSelectContact = (id: string) => {
    const affiliate = affiliates.find((item) => item.id === id);
    if (affiliate) {
      if (entryIntent === 'affiliate-add') {
        setAffiliateAddDraft({
          firstName: affiliate.name.trim().split(/\s+/)[0] ?? '',
          discountCode: '',
        });
      }
      setTarget({ type: 'affiliate', data: affiliate });
    }
  };

  const handleSelectRawEmail = (email: string) => {
    if (entryIntent === 'affiliate-add') {
      setAffiliateAddDraft({ firstName: '', discountCode: '' });
    }
    setTarget({ type: 'email', data: { email } });
  };

  return (
    <VStack align="stretch" gap={6}>
      <Box>
        <Text fontSize="lg" fontWeight="semibold" mb={2}>
          {t('invitations.modal.inputSelection.title')}
        </Text>
        <Text fontSize="sm" color="fg.muted" mb={4}>
          {t('invitations.modal.inputSelection.description')}
        </Text>

        <RecipientPicker
          options={options}
          isLoading={isLoading}
          isFetching={isFetching}
          searchValue={search}
          onSearchChange={setSearch}
          onSelectContact={handleSelectContact}
          onSelectRawEmail={handleSelectRawEmail}
          placeholder={t('invitations.modal.inputSelection.placeholder')}
          emptyHint={emptyHint}
          rawEmailActionLabel={t('invitations.modal.inputSelection.useEmail')}
        />
      </Box>
    </VStack>
  );
};
